import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { toast } from "react-toastify";
import { useUserContext } from "~/hooks/useUserContext";
import { hasRead } from "~/api/v2/requests";
import { Info, CheckCircle2 } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "~/atoms/alert";

type Props = {
  documentVersionId: number;
  onConfirm: () => void;
  isDone?: boolean;
};

export function DocumentReaderConfirmation({
  documentVersionId,
  onConfirm,
  isDone = false,
}: Props) {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await hasRead(documentVersionId, user.id);
      onConfirm();
      toast.success(t(($) => $.commons.workflow.documentMarkedAsRead));
    } catch (error) {
      console.error(
        t(($) => $.commons.workflow.failedToConfirmDocumentReading),
        error,
      );
      toast.error(t(($) => $.commons.workflow.failedToConfirmDocumentReading));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {isDone ? (
        <Alert variant="default">
          <CheckCircle2 className="text-success!" />
          <AlertTitle>
            {t(($) => $.commons.workflow.documentConfirmed)}
          </AlertTitle>
          <AlertDescription>
            <span>
              {t(($) => $.commons.workflow.documentConfirmedDescription)}
            </span>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="warn">
          <Info className="text-warning-foreground!" />
          <AlertTitle>
            {t(($) => $.commons.workflow.documentReadyForReview)}
          </AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>
              {t(($) => $.commons.workflow.pleaseConfirmDocumentRead)}
            </span>
            <Button size="sm" onClick={handleConfirm} disabled={isSubmitting}>
              {t(($) => $.commons.workflow.readDocument)}
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
